// ---------------------------------------------------------------------------
// Period to Dates Converter
// ---------------------------------------------------------------------------
// Converts GlobalTimeBar period strings to API date_from/date_to parameters
// ---------------------------------------------------------------------------

import type { TimePeriod } from '@/components/v11/discovery/types'

export type { TimePeriod }

export interface DateRange {
  readonly date_from?: string
  readonly date_to?: string
}

/**
 * Convert period string to date range for API filters
 *
 * @param period - Time period ('7d', '30d', '3m', '6m', '12m', 'all')
 * @returns Date range object with date_from and date_to in YYYY-MM-DD format
 */
export function periodToDates(period: TimePeriod): DateRange {
  // Special cases
  if (period === 'all') {
    return {} // No date filter
  }

  if (period === 'custom') {
    // Custom dates are passed through from URL state
    return {}
  }

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()) // Start of today
  const dateTo = now.toISOString().split('T')[0]

  let dateFrom: Date

  switch(period) {
    case 'today':
      dateFrom = new Date(today)
      break

    case 'yesterday':
      dateFrom = new Date(today)
      dateFrom.setDate(dateFrom.getDate() - 1)
      return {
        date_from: dateFrom.toISOString().split('T')[0],
        date_to: dateFrom.toISOString().split('T')[0]
      }

    case '7d':
      dateFrom = new Date(today)
      dateFrom.setDate(dateFrom.getDate() - 7)
      break

    case '15d':
      dateFrom = new Date(today)
      dateFrom.setDate(dateFrom.getDate() - 15)
      break

    case '30d':
      dateFrom = new Date(today)
      dateFrom.setDate(dateFrom.getDate() - 30)
      break

    case '3m':
      dateFrom = new Date(today)
      dateFrom.setMonth(dateFrom.getMonth() - 3)
      break

    case '6m':
      dateFrom = new Date(today)
      dateFrom.setMonth(dateFrom.getMonth() - 6)
      break

    case '12m':
      dateFrom = new Date(today)
      dateFrom.setMonth(dateFrom.getMonth() - 12)
      break

    case 'wtd': // Week to Date (Monday to today)
      dateFrom = new Date(today)
      const dayOfWeek = dateFrom.getDay()
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // Sunday = 0, Monday = 1
      dateFrom.setDate(dateFrom.getDate() - daysToMonday)
      break

    case 'mtd': // Month to Date (1st of month to today)
      dateFrom = new Date(today.getFullYear(), today.getMonth(), 1)
      break

    case 'qtd': // Quarter to Date (1st of quarter to today)
      const quarter = Math.floor(today.getMonth() / 3)
      const quarterStartMonth = quarter * 3
      dateFrom = new Date(today.getFullYear(), quarterStartMonth, 1)
      break

    case 'ytd': // Year to Date (Jan 1st to today)
      dateFrom = new Date(today.getFullYear(), 0, 1)
      break

    default:
      // Unknown period, default to last 30 days
      dateFrom = new Date(today)
      dateFrom.setDate(dateFrom.getDate() - 30)
  }

  return {
    date_from: dateFrom.toISOString().split('T')[0],
    date_to: dateTo
  }
}

/**
 * Get human-readable label for period
 */
export function periodToLabel(period: TimePeriod): string {
  const labels: Record<TimePeriod, string> = {
    'custom': 'Custom range',
    'today': 'Today',
    'yesterday': 'Yesterday',
    '7d': 'Last 7 days',
    '15d': 'Last 15 days',
    '30d': 'Last 30 days',
    '3m': 'Last 3 months',
    '6m': 'Last 6 months',
    '12m': 'Last 12 months',
    'wtd': 'Week to date',
    'mtd': 'Month to date',
    'qtd': 'Quarter to date',
    'ytd': 'Year to date',
    'all': 'All time'
  }

  return labels[period] || labels['30d']
}
